import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "../components/atlas/helpers";
import { useAtlas } from "../store/atlasStore";
import { RefreshCw, TrendingUp, TrendingDown, Minus } from "lucide-react";

function fmt(v, d = 3) {
  if (v == null || !isFinite(+v)) return "—";
  return Number(v).toFixed(d);
}

function pct(v, showSign = true) {
  if (v == null || !isFinite(+v)) return "—";
  const n = Number(v);
  return `${showSign && n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
}

function FamilyContributionChart({ contribution }) {
  if (!contribution) return null;
  
  // Filter out zero entries and sort descending
  const entries = Object.entries(contribution)
    .filter(([, val]) => val > 0)
    .sort(([, a], [, b]) => b - a);

  if (entries.length === 0) {
    return <div style={{ fontSize: 10, color: "var(--text-tertiary)", padding: 10, textAlign: "center" }}>No active family contributions</div>;
  }

  // Vibrant, curated quantitative charting colors
  const colors = [
    "hsl(210, 85%, 55%)",  // Tech Blue
    "hsl(145, 70%, 45%)",  // Forest Green
    "hsl(340, 80%, 55%)",  // Rose Red
    "hsl(38, 90%, 50%)",   // Amber Orange
    "hsl(275, 75%, 60%)",  // Indigo Purple
    "hsl(180, 80%, 40%)",  // Dark Teal
    "hsl(52, 95%, 45%)",   // Yellow Gold
    "hsl(15, 85%, 50%)",   // Coral Brick
  ];

  const total = entries.reduce((acc, [, val]) => acc + val, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "10px" }}>
      {/* Stacked Percentage Bar Chart */}
      <div style={{ display: "flex", height: 12, borderRadius: 6, overflow: "hidden", background: "var(--border-dim)", border: "1px solid var(--border-dim)" }}>
        {entries.map(([name, val], i) => {
          const widthPct = (val / total) * 100;
          return (
            <div
              key={name}
              style={{
                width: `${widthPct}%`,
                background: colors[i % colors.length],
                height: "100%",
                transition: "width 0.4s ease",
              }}
              title={`${name}: ${val.toFixed(1)}%`}
            />
          );
        })}
      </div>

      {/* Legend list */}
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {entries.map(([name, val], i) => {
          const widthPct = (val / total) * 100;
          const col = colors[i % colors.length];
          return (
            <div key={name} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0, flex: 1 }}>
                <div style={{ width: 6, height: 6, borderRadius: "50%", background: col, flexShrink: 0 }} />
                <span style={{ color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {name.replace(/([A-Z])/g, " $1").trim()}
                </span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span className="mono" style={{ color: "var(--text-primary)", fontWeight: 600 }}>{val.toFixed(1)}%</span>
                <span className="mono" style={{ color: "var(--text-tertiary)", fontSize: 8 }}>({widthPct.toFixed(0)}% rel)</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SignalFamily({ fam }) {
  const [open, setOpen] = useState(true);
  const dir = fam.direction > 0 ? "UP" : fam.direction < 0 ? "DOWN" : "FLAT";
  
  const borderLeftColor = dir === "UP" ? "var(--green)" : dir === "DOWN" ? "var(--red)" : "var(--text-tertiary)";
  const pillStyle = dir === "UP"
    ? { background: "rgba(22, 163, 74, 0.15)", border: "1px solid rgba(22, 163, 74, 0.3)", color: "rgb(74, 222, 128)", fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }
    : dir === "DOWN"
    ? { background: "rgba(220, 38, 38, 0.15)", border: "1px solid rgba(220, 38, 38, 0.3)", color: "rgb(248, 113, 113)", fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 700 }
    : { background: "rgba(100, 116, 139, 0.15)", border: "1px solid rgba(100, 116, 139, 0.3)", color: "rgb(203, 213, 225)", fontSize: 8, padding: "1px 5px", borderRadius: 3, fontWeight: 700 };

  return (
    <div style={{ marginBottom: 8, border: "1px solid var(--border-dim)", borderLeft: `3px solid ${borderLeftColor}`, borderRadius: "0 4px 4px 0", background: "var(--bg-raised)", overflow: "hidden" }}>
      {/* Family Header */}
      <div
        style={{ display: "flex", alignItems: "center", gap: 8, padding: "5px 8px", background: "var(--surface-2)", cursor: "pointer", borderBottom: "1px solid var(--border-dim)" }}
        onClick={() => setOpen(!open)}
      >
        <span style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", color: "var(--text-primary)", flex: 1 }}>{fam.name}</span>
        <span style={pillStyle}>{dir}</span>
        <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono'", color: "var(--text-tertiary)" }}>str: {fmt(fam.strength, 2)}</span>
        <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono'", color: "var(--text-tertiary)" }}>conf: {fmt(fam.confidence, 2)}</span>
      </div>

      {/* Evidence list */}
      {open && fam.evidence && (
        <div style={{ padding: 4 }}>
          {fam.evidence.map((ev, idx) => {
            const val = ev.value ?? 0;
            const isPos = val > 0;
            const isNeg = val < 0;
            const barPct = Math.min(Math.abs(val) * 100 / 2, 50);
            return (
              <div key={idx} style={{ display: "flex", alignItems: "center", gap: 6, padding: "4px 6px", borderBottom: idx < fam.evidence.length - 1 ? "1px solid var(--border-dim)" : "none" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                    <span className="mono" style={{ fontSize: 9, color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }} title={ev.feature_id}>
                      {ev.feature_id?.replace("price.", "").replace("volume.", "")}
                    </span>
                    <span className="mono" style={{ fontSize: 9, color: isPos ? "var(--green)" : isNeg ? "var(--red)" : "var(--text-tertiary)", fontWeight: 600 }}>
                      {fmt(val, 3)}
                    </span>
                  </div>
                  {ev.argument && (
                    <div style={{ fontSize: 8, color: "var(--text-tertiary)" }}>{ev.argument}</div>
                  )}
                </div>
                <div style={{ width: 50, height: 4, background: "var(--border-dim)", borderRadius: 2, position: "relative", flexShrink: 0 }}>
                  <div style={{
                    position: "absolute", top: 0, bottom: 0, borderRadius: 2,
                    background: isPos ? "var(--green)" : isNeg ? "var(--red)" : "var(--text-tertiary)",
                    left: isPos ? "50%" : `${50 - barPct}%`,
                    width: `${barPct}%`,
                  }} />
                </div>
                <span className="mono" style={{ width: 28, textAlign: "right", fontSize: 9, color: "var(--text-tertiary)", flexShrink: 0 }}>
                  w:{fmt(ev.weight, 2)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function VerdictPanel({ result, requestedHorizon }) {
  if (!result) return null;
  const dir = result.decision || "WAIT";
  const conf = result.confidence ?? 0;
  
  // Custom glowing gradients/colors for premium UI feel
  const pillStyle = dir === "UP"
    ? {
        background: "linear-gradient(135deg, rgba(22, 163, 74, 0.22) 0%, rgba(22, 163, 74, 0.05) 100%)",
        border: "1px solid rgba(22, 163, 74, 0.45)",
        color: "rgb(74, 222, 128)",
        boxShadow: "0 0 12px rgba(22, 163, 74, 0.12)",
        fontSize: 16, padding: "5px 16px", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700
      }
    : dir === "DOWN"
    ? {
        background: "linear-gradient(135deg, rgba(220, 38, 38, 0.22) 0%, rgba(220, 38, 38, 0.05) 100%)",
        border: "1px solid rgba(220, 38, 38, 0.45)",
        color: "rgb(248, 113, 113)",
        boxShadow: "0 0 12px rgba(220, 38, 38, 0.12)",
        fontSize: 16, padding: "5px 16px", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700
      }
    : {
        background: "linear-gradient(135deg, rgba(100, 116, 139, 0.22) 0%, rgba(100, 116, 139, 0.05) 100%)",
        border: "1px solid rgba(100, 116, 139, 0.45)",
        color: "rgb(203, 213, 225)",
        fontSize: 16, padding: "5px 16px", borderRadius: 4, display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700
      };

  const isStable = result.risk?.regime_stability === "Stable";
  const scoreVal = result.score ?? 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Direction */}
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={pillStyle}>
          {dir === "UP" ? <TrendingUp size={15} /> : dir === "DOWN" ? <TrendingDown size={15} /> : <Minus size={15} />}
          {dir}
        </span>
        <div>
          <div style={{ fontSize: 24, fontFamily: "'JetBrains Mono'", fontWeight: 700, lineHeight: 1, color: "var(--text-primary)" }}>
            {(conf * 100).toFixed(1)}%
          </div>
          <div style={{ fontSize: 9, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Confidence</div>
        </div>
      </div>

      {/* Key stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 4 }}>
        {[
          { label: "Score",    val: fmt(result.score, 3), col: scoreVal > 0 ? "var(--green)" : scoreVal < 0 ? "var(--red)" : undefined },
          { label: "Horizon",  val: `${requestedHorizon} bars` },
          { label: "Regime",   val: result.risk?.regime_stability || "—", col: isStable ? "var(--green)" : "var(--amber)" },
          { label: "Analogue", val: result.analogues?.analogues?.length ? `${result.analogues.analogues.length} found` : "—" },
          { label: "ML P(Up)", val: result.ml_advisor?.probability_up != null ? `${(result.ml_advisor.probability_up*100).toFixed(1)}%` : "—" },
          { label: "Models",   val: result.model_agreement?.per_model?.length ?? "—" },
        ].map(m => (
          <div key={m.label} className="stat-card" style={{ padding: "5px 7px" }}>
            <div className="s-label">{m.label}</div>
            <div className="s-val" style={{ fontSize: 12, color: m.col || "var(--text-primary)" }}>{m.val}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DecisionWorkspace() {
  const { state } = useAtlas();
  const { provider, symbol, interval } = state;

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [mlAdvisor, setMlAdvisor] = useState(false);
  const [useLearnedWeights, setUseLearnedWeights] = useState(false);
  const [useRegimeConditional, setUseRegimeConditional] = useState(false);
  const [horizon, setHorizon] = useState(5);
  const [peerSymbol, setPeerSymbol] = useState("");

  // Center column tabs: "signals" | "analogues" | "weights"
  const [activeTab, setActiveTab] = useState("signals");

  // Adaptive learned weights state
  const [weightsResult, setWeightsResult] = useState(null);
  const [weightsLoading, setWeightsLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const body = {
        provider, symbol, interval, period: "2y", horizon,
        include_models: true,
        use_learned_weights: useLearnedWeights,
        use_regime_conditional_weights: useRegimeConditional,
        ml_advisor: mlAdvisor,
        analogue_schema: "v2",
      };
      if (peerSymbol) { body.peer_provider = provider; body.peer_symbol = peerSymbol; }

      const [recR, histR] = await Promise.allSettled([
        axios.post(`${API}/decision/recommend`, body),
        axios.get(`${API}/decision/history`, { params: { limit: 20 } }),
      ]);
      if (recR.status === "fulfilled") setResult(recR.value.data);
      if (histR.status === "fulfilled") setHistory(histR.value.data);
    } catch (e) {
      setResult({ error: e.message });
    }
    setLoading(false);
  }, [provider, symbol, interval, horizon, useLearnedWeights, useRegimeConditional, mlAdvisor, peerSymbol]);

  // Automatic fetching removed, user must trigger manually via the button.

  const learnWeights = async () => {
    setWeightsLoading(true);
    try {
      const body = {
        provider, symbol, interval, period: "2y", horizon,
        window: 200,
      };
      if (peerSymbol) { body.peer_provider = provider; body.peer_symbol = peerSymbol; }
      const r = await axios.post(`${API}/decision/learned_weights`, body);
      setWeightsResult(r.data);
    } catch (e) {
      setWeightsResult({ error: e.response?.data?.detail || e.message });
    }
    setWeightsLoading(false);
  };

  const modelAgreement = result?.model_agreement;
  const hasSignals = result?.signals && Array.isArray(result.signals) && result.signals.length > 0;

  // Analogue variables mapping
  const analoguesData = result?.analogues || {};
  const analoguesList = analoguesData.analogues || [];
  const analoguesSummary = analoguesData.summary || {};

  return (
    <div style={{ display: "grid", gridTemplateColumns: "340px 1fr 280px", gridTemplateRows: "auto 1fr", height: "100%", gap: 4, padding: 4 }}>

      {/* ── LEFT: Verdict + controls + dynamic contribution ────────────────────────── */}
      <div style={{ gridRow: "1 / 3", display: "flex", flexDirection: "column", gap: 4 }}>
        {/* Controls */}
        <div className="panel" style={{ flexShrink: 0 }}>
          <div className="panel-header">
            <span className="panel-title">Decision Engine</span>
            <button className="btn btn-primary" style={{ fontSize: 10, padding: "2px 8px", height: "auto", minHeight: 0 }} onClick={fetch} disabled={loading}>
              {loading ? <RefreshCw size={11} className="spin" style={{ marginRight: 4 }} /> : null}
              {loading ? "Running..." : "Run Decision Engine"}
            </button>
          </div>
          <div className="panel-body" style={{ padding: "8px 10px" }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 8 }}>
              <div>
                <div className="label" style={{ marginBottom: 2 }}>Horizon (bars)</div>
                <input type="number" className="inp" value={horizon} min={1} max={60}
                  onChange={e => setHorizon(+e.target.value || 5)} style={{ height: 24 }} />
              </div>
              <div>
                <div className="label" style={{ marginBottom: 2 }}>Peer Symbol</div>
                <input type="text" className="inp" value={peerSymbol} placeholder="e.g. BTC-USD"
                  onChange={e => setPeerSymbol(e.target.value)} style={{ height: 24 }} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                [mlAdvisor, setMlAdvisor, "ML Advisor"],
                [useLearnedWeights, setUseLearnedWeights, "Learned Weights"],
                [useRegimeConditional, setUseRegimeConditional, "Regime Conditional"],
              ].map(([val, setter, label]) => (
                <label key={label} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 10 }}>
                  <input type="checkbox" checked={val} onChange={e => { setter(e.target.checked); }} style={{ accentColor: "var(--blue)", width: 11, height: 11 }} />
                  <span style={{ color: val ? "var(--text-primary)" : "var(--text-tertiary)" }}>{label}</span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Verdict */}
        <div className="panel" style={{ flexShrink: 0 }}>
          <div className="panel-header"><span className="panel-title">Verdict</span></div>
          <div className="panel-body" style={{ padding: "10px" }}>
            {loading && !result && (
              <div style={{ color: "var(--text-tertiary)", fontSize: 11 }}>Computing recommendation…</div>
            )}
            {result?.error && (
              <div style={{ color: "var(--red)", fontSize: 11 }}>Error: {result.error}</div>
            )}
            {result && !result.error && <VerdictPanel result={result} requestedHorizon={horizon} />}
          </div>
        </div>

        {/* Dynamic Decision Contribution Bar Chart */}
        {result && !result.error && result.family_contribution && (
          <div className="panel" style={{ flexShrink: 0 }}>
            <div className="panel-header"><span className="panel-title">Decision Contribution</span></div>
            <FamilyContributionChart contribution={result.family_contribution} />
          </div>
        )}

        {/* ML Advisor detail */}
        {result?.ml_advisor && (
          <div className="panel" style={{ flexShrink: 0 }}>
            <div className="panel-header"><span className="panel-title">ML Advisor</span></div>
            <div className="panel-body" style={{ padding: "6px 10px", fontSize: 10 }}>
              {result.ml_advisor.available ? (
                <>
                  <div className="kv-row"><span className="k">P(Up)</span><span className="v">{(result.ml_advisor.probability_up*100).toFixed(1)}%</span></div>
                  <div className="kv-row"><span className="k">Models</span><span className="v">{result.ml_advisor.n_models}</span></div>
                  <div className="kv-row"><span className="k">Kind</span><span className="v">{result.ml_advisor.kind || "—"}</span></div>
                  <div className="kv-row"><span className="k">Best Model</span><span className="v mono" style={{ fontSize: 9 }}>{result.ml_advisor.best_model_id?.slice(0,10)}</span></div>
                </>
              ) : (
                <div style={{ color: "var(--text-tertiary)" }}>{result.ml_advisor.reason || "Unavailable"}</div>
              )}
            </div>
          </div>
        )}

        {/* Model ensemble */}
        {modelAgreement && !modelAgreement.error && modelAgreement.consensus_direction && (
          <div className="panel" style={{ flexShrink: 0 }}>
            <div className="panel-header"><span className="panel-title">Model Ensemble Vote</span></div>
            <div className="panel-body" style={{ padding: "6px 10px", fontSize: 10 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 6, alignItems: "center" }}>
                <span className={`badge ${modelAgreement.consensus_direction === "LONG" ? "badge-green" : modelAgreement.consensus_direction === "SHORT" ? "badge-red" : ""}`}>
                  {modelAgreement.consensus_direction}
                </span>
                <span style={{ color: "var(--text-tertiary)" }}>
                  {modelAgreement.votes?.LONG || 0}↑ {modelAgreement.votes?.SHORT || 0}↓ of {modelAgreement.per_model?.length || 0}
                </span>
              </div>
              {(modelAgreement.per_model || []).map((m, i) => (
                <div key={i} className="kv-row">
                  <span className="k">{m.name}</span>
                  <span className="v" style={{ color: m.vote === "LONG" ? "var(--green)" : m.vote === "SHORT" ? "var(--red)" : "var(--text-secondary)" }}>
                    {m.vote} {m.prob_up != null ? `(${(m.prob_up*100).toFixed(0)}%)` : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── CENTER: Signal Decomposition & Nested Workspace tabs ─────── */}
      <div className="panel" style={{ gridRow: "1 / 3", display: "flex", flexDirection: "column" }}>
        <div className="panel-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span className="panel-title" style={{ fontSize: 11 }}>Analysis Console</span>
          <div style={{ display: "flex", gap: 2, background: "var(--bg-raised)", padding: 2, borderRadius: 4, border: "1px solid var(--border-dim)" }}>
            {[
              ["signals", "Signals"],
              ["analogues", "Analogues (Top 10)"],
              ["weights", "Adaptive Weights"],
            ].map(([id, label]) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`btn ${activeTab === id ? "btn-primary" : "btn-ghost"}`}
                style={{ fontSize: 9, padding: "2px 8px", height: "auto", minHeight: 0, textTransform: "none" }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <div className="panel-body" style={{ overflowY: "auto", flex: 1, padding: 8 }}>
          {/* TAB 1: SIGNALS */}
          {activeTab === "signals" && (
            <>
              {hasSignals ? (
                <>
                  <div className="section-sep" style={{ marginTop: 0 }}><span className="s-label">Signal Families & Evidence</span></div>
                  {result.signals.map((fam, idx) => (
                    <SignalFamily key={idx} fam={fam} />
                  ))}

                  {/* Feature snapshot */}
                  {result?.feature_snapshot && (
                    <div style={{ marginTop: 12 }}>
                      <div className="section-sep"><span className="s-label">Feature Snapshot</span></div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 8px" }}>
                        {Object.entries(result.feature_snapshot)
                          .filter(([, v]) => typeof v === "number" && isFinite(v))
                          .slice(0, 30)
                          .map(([k, v]) => (
                            <div className="kv-row" key={k} style={{ border: "none", padding: "1px 0" }}>
                              <span className="k" style={{ overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160, fontSize: 9 }}>{k.replace("price.", "").replace("volume.", "")}</span>
                              <span className="v mono" style={{ fontSize: 9 }}>{fmt(v, 4)}</span>
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ color: "var(--text-tertiary)", fontSize: 11, padding: 8 }}>
                  {loading ? "Computing signals…" : "No signal data available."}
                </div>
              )}
            </>
          )}

          {/* TAB 2: ANALOGUES */}
          {activeTab === "analogues" && (
            <>
              {analoguesSummary.k ? (
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* Summary Metric Cards */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 4, marginBottom: 12 }}>
                    {[
                      { label: "Median Ret", val: pct(analoguesSummary.median_return_pct) },
                      { label: "Hit Rate", val: analoguesSummary.hit_rate != null ? `${(analoguesSummary.hit_rate * 100).toFixed(0)}%` : "—" },
                      { label: "Best Case", val: pct(analoguesSummary.best_case_pct), col: "var(--green)" },
                      { label: "Worst Case", val: pct(analoguesSummary.worst_case_pct), col: "var(--red)" },
                    ].map(m => (
                      <div key={m.label} className="stat-card" style={{ padding: "6px 8px" }}>
                        <div className="s-label" style={{ fontSize: 8 }}>{m.label}</div>
                        <div className="s-val" style={{ fontSize: 12, fontWeight: 700, color: m.col || "var(--text-primary)" }}>{m.val}</div>
                      </div>
                    ))}
                  </div>

                  {/* Top 10 Analogue List */}
                  <div className="section-sep"><span className="s-label">Top 10 Historical Matches ({analoguesData.schema || "v2"})</span></div>
                  <div className="table-container" style={{ flex: 1 }}>
                    <table className="dt">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th className="r">Distance</th>
                          <th className="r">Forward Return</th>
                        </tr>
                      </thead>
                      <tbody>
                        {analoguesList.slice(0, 10).map((a, i) => {
                          const ret = a.forward_return_pct ?? a.outcome ?? 0;
                          const dist = a.distance ?? a.similarity ?? 0;
                          return (
                            <tr key={i}>
                              <td className="mono" style={{ fontSize: 9 }}>{a.date?.slice(0, 10)}</td>
                              <td className="r mono">{fmt(dist, 4)}</td>
                              <td className="r mono" style={{ color: ret >= 0 ? "var(--green)" : "var(--red)", fontWeight: 600 }}>
                                {pct(ret)}
                              </td>
                            </tr>
                          );
                        })}
                        {!analoguesList.length && (
                          <tr><td colSpan={3} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 16 }}>No analogues found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ color: "var(--text-tertiary)", fontSize: 11, padding: 8, textAlign: "center", marginTop: 24 }}>
                  {loading ? "Loading analogues..." : "Not enough historical data to generate analogues."}
                </div>
              )}
            </>
          )}

          {/* TAB 3: ADAPTIVE WEIGHTS */}
          {activeTab === "weights" && (
            <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
              {!weightsResult ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "48px 24px", flex: 1, color: "var(--text-tertiary)" }}>
                  <p style={{ fontSize: 10, marginBottom: 12, textAlign: "center", letterSpacing: "0.05em", color: "var(--text-secondary)" }}>
                    DYNAMICALLY FIT SIGNAL WEIGHTS BASED ON HISTORICAL PRICE ACTION.
                  </p>
                  <button className="btn btn-primary" onClick={learnWeights} disabled={weightsLoading}>
                    {weightsLoading ? <RefreshCw size={11} className="spin" style={{ marginRight: 6 }} /> : null}
                    {weightsLoading ? "Fitting..." : "Fit Adaptive Weights"}
                  </button>
                </div>
              ) : weightsResult.error ? (
                <div style={{ color: "var(--red)", padding: 8, fontFamily: "monospace", fontSize: 10 }}>
                  Error fitting weights: {weightsResult.error}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column" }}>
                  {/* Fitting Stats */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 4, marginBottom: 12 }}>
                    {[
                      { label: "In-Sample Acc", val: `${(weightsResult.in_sample_accuracy * 100).toFixed(1)}%` },
                      { label: "Lookback Window", val: `${weightsResult.window} bars` },
                      { label: "Data Coverage", val: `${(weightsResult.coverage * 100).toFixed(0)}%` },
                    ].map(m => (
                      <div key={m.label} className="stat-card" style={{ padding: "6px 8px" }}>
                        <div className="s-label" style={{ fontSize: 8 }}>{m.label}</div>
                        <div className="s-val" style={{ fontSize: 12, fontWeight: 700 }}>{m.val}</div>
                      </div>
                    ))}
                  </div>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                    <span className="section-title" style={{ fontSize: 10, fontWeight: 600, color: "var(--text-secondary)" }}>Comparative Weights</span>
                    <button className="btn btn-ghost" style={{ fontSize: 9, padding: "2px 6px" }} onClick={learnWeights} disabled={weightsLoading}>
                      Refit
                    </button>
                  </div>

                  {/* Weights Comparison Table */}
                  <div className="table-container">
                    <table className="dt">
                      <thead>
                        <tr>
                          <th>Signal Group</th>
                          <th className="r">Manual</th>
                          <th className="r">Learned</th>
                          <th className="r">Delta</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(weightsResult.learned_weights || {}).map(([name, lw]) => {
                          const mw = weightsResult.manual_weights?.[name] || 0;
                          const diff = lw - mw;
                          return (
                            <tr key={name}>
                              <td style={{ fontSize: 9, color: "var(--text-secondary)" }}>{name.replace(/_/g, " ")}</td>
                              <td className="r mono">{fmt(mw, 3)}</td>
                              <td className="r mono" style={{ fontWeight: 600 }}>{fmt(lw, 3)}</td>
                              <td className="r mono" style={{ color: diff > 0 ? "var(--green)" : diff < 0 ? "var(--red)" : "var(--text-tertiary)" }}>
                                {diff > 0 ? "+" : ""}{fmt(diff, 3)}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ── RIGHT: Decision History ───────────────────────────────────── */}
      <div className="panel" style={{ gridRow: "1 / 3" }}>
        <div className="panel-header">
          <span className="panel-title">Decision History</span>
        </div>
        <div className="table-container">
          <table className="dt">
            <thead>
              <tr>
                <th>Time</th>
                <th>Dir</th>
                <th className="r">Conf</th>
                <th className="r">Score</th>
              </tr>
            </thead>
            <tbody>
              {history.map((h, i) => (
                <tr key={i}>
                  <td style={{ fontSize: 9, color: "var(--text-tertiary)" }}>{h.created_at?.slice(0,16).replace("T"," ")}</td>
                  <td>
                    <span className={`badge ${h.decision === "UP" ? "badge-green" : h.decision === "DOWN" ? "badge-red" : ""}`}>
                      {h.decision}
                    </span>
                  </td>
                  <td className="r">{h.confidence != null ? `${(h.confidence*100).toFixed(0)}%` : "—"}</td>
                  <td className="r">{fmt(h.score, 3)}</td>
                </tr>
              ))}
              {!history.length && (
                <tr><td colSpan={4} style={{ textAlign: "center", color: "var(--text-tertiary)", padding: 16 }}>No history</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
